import React, { useState } from 'react'
import styles from './Footer.module.css'

/* Use a white / light version of the logo so it reads on the dark footer.
   Or pass it in: <Footer logo="https://your-link/logoWhite.png" /> */
import LOGO from '../../assets/logoWhite.png'
import { buildEnquiryEmailHtml } from '../../emailTemplates'

const EXPLORE = [
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Work', href: '#work' },
  { label: 'Contact', href: '#contact' },
]

const CAPABILITIES = [
  'MICE & Conferences',
  'Award Nights & Engagement',
  'Strategic Consulting',
  'Group Travel & Off-sites',
]

const EVENT_TYPES = [
  'MICE & Conference',
  'Award Night',
  'Product Launch',
  'Off-site / Group Travel',
  'Strategic Consulting',
  'Other',
]

const initialForm = { name: '', email: '', phone: '', company: '', eventType: '', message: '' }

// Where your enquiry emails get sent to (your inbox), and which backend to call
const ENQUIRY_RECEIVER = 'dinesh.hirve@sociallysaucd.com,bhavik@collectivekatalyx.com'
const API_URL = process.env.REACT_APP_API_URL
const API_KEY = process.env.REACT_APP_API_KEY

const Footer = ({ logo = LOGO }) => {
  const year = new Date().getFullYear()
  const [form, setForm] = useState(initialForm)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMsg('')
    setIsSubmitting(true)

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
        },
        body: JSON.stringify({
          to: ENQUIRY_RECEIVER,
          subject: `New Enquiry — ${form.eventType || 'General'} from ${form.name}`,
          html: buildEnquiryEmailHtml(form),
          replyTo: form.email,
        }),
      })

      const result = await res.json()

      if (!res.ok || !result.success) {
        throw new Error(result.error || 'Something went wrong. Please try again.')
      }

      setShowSuccess(true)
      setForm(initialForm)
    } catch (err) {
      console.error('Enquiry submit failed:', err)
      setErrorMsg('Could not send your message right now. Please try again or email us directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <footer id="contact" className={styles.footer}>
      <div className={styles.container}>
        {/* --- Contact band --- */}
        <div className={styles.contact}>
          {/* Left: copy + details */}
          <div className={styles.info}>
            <span className={styles.eyebrow}>Let&apos;s Begin</span>
            <h2 className={styles.heading}>
              Have an event or strategy challenge in mind?
            </h2>
            <p className={styles.sub}>
              Tell us a little about it and we&apos;ll get back to you within one
              business day.
            </p>

            <ul className={styles.details}>
              <li>
                <span className={styles.detailLabel}>Email</span>
                <a href="mailto:bhavik@collectivekatalyx.com" className={styles.detailLink}>
                  bhavik@collectivekatalyx.com
                </a>
              </li>
              <li>
                <span className={styles.detailLabel}>Phone</span>
                <a href="tel:+917030126293" className={styles.detailLink}>
        
                  +91 70301 26293
                </a>
              </li>
              <li>
                <span className={styles.detailLabel}>Studio</span>
                <span className={styles.detailText}>Pune, Maharashtra · India</span>
              </li>
            </ul>
          </div>

          {/* Right: form card */}
          <div className={styles.formCard}>
            <form onSubmit={handleSubmit} className={styles.form} noValidate>
              <div className={styles.row}>
                <label className={styles.field}>
                  <span className={styles.label}>Name</span>
                  <input
                    type="text" name="name" value={form.name} onChange={handleChange}
                    placeholder="Your name" className={styles.input} required
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Email</span>
                  <input
                    type="email" name="email" value={form.email} onChange={handleChange}
                    placeholder="you@company.com" className={styles.input} required
                  />
                </label>
              </div>

              <div className={styles.row}>
                <label className={styles.field}>
                  <span className={styles.label}>Phone Number</span>
                  <input
                    type="tel" name="phone" value={form.phone} onChange={handleChange}
                    placeholder="+91 " className={styles.input}
                    pattern="[0-9+\-\s]{7,15}" required
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Company</span>
                  <input
                    type="text" name="company" value={form.company} onChange={handleChange}
                    placeholder="Company name" className={styles.input}
                  />
                </label>
              </div>

              <label className={styles.field}>
                <span className={styles.label}>Event Type</span>
                <select
                  name="eventType" value={form.eventType} onChange={handleChange}
                  className={styles.input} required
                >
                  <option value="" disabled>Select one</option>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>

              <label className={styles.field}>
                <span className={styles.label}>Message</span>
                <textarea
                  name="message" value={form.message} onChange={handleChange}
                  placeholder="Tell us about your event or challenge…"
                  className={`${styles.input} ${styles.textarea}`} rows={3} required
                />
              </label>

              {errorMsg && (
                <p className={styles.errorText} role="alert">{errorMsg}</p>
              )}

              <button type="submit" className={styles.submit} disabled={isSubmitting}>
                {isSubmitting ? 'Sending…' : 'Send Message'}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </form>
          </div>
        </div>

        <hr className={styles.divider} />

        {/* --- Columns --- */}
        <div className={styles.grid}>
          <div className={styles.brandCol}>
            <a href="#home" className={styles.logo}>
              <img src={logo} alt="Collective KatalyX" className={styles.logoImg} />
            </a>
            <p className={styles.tagline}>
              A complete experience studio — designing one-of-a-kind events that
              inspire, engage, and deliver measurable impact across India.
            </p>
            <div className={styles.socials}>
              <a target="_blank" href="https://www.linkedin.com/company/collective-katalyx/" aria-label="LinkedIn" className={styles.social}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M4.98 3.5A2.5 2.5 0 1 0 5 8.5 2.5 2.5 0 0 0 4.98 3.5zM3 9h4v12H3zM10 9h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05C21.4 8.65 22 11.1 22 14.3V21h-4v-5.9c0-1.4 0-3.2-2-3.2s-2.3 1.5-2.3 3.1V21h-4z" />
                </svg>
              </a>
              <a target="_blank" href="#" aria-label="Instagram" className={styles.social}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                </svg>
              </a>
            </div>
          </div>

          <div className={styles.linkCol}>
            <h3 className={styles.colTitle}>Explore</h3>
            <ul>
              {EXPLORE.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className={styles.link}>{l.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.linkCol}>
            <h3 className={styles.colTitle}>Capabilities</h3>
            <ul>
              {CAPABILITIES.map((c) => (
                <li key={c}><span className={styles.linkStatic}>{c}</span></li>
              ))}
            </ul>
          </div>
        </div>

        {/* --- Bottom bar --- */}
        <div className={styles.bottom}>
          <span className={styles.signoff}>Ideate. Curate. Execute.</span>
          <span className={styles.copyright}>
            © {year} Collective KatalyX. All rights reserved.
          </span>
        </div>
      </div>

      {/* --- Success popup --- */}
      {showSuccess && (
        <div
          className={styles.overlay}
          onClick={() => setShowSuccess(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <span className={styles.checkCircle}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <h3 className={styles.modalTitle}>Message submitted successfully</h3>
            <p className={styles.modalText}>
              Thanks for reaching out — our team will get back to you within one
              business day.
            </p>
            <button className={styles.modalButton} onClick={() => setShowSuccess(false)}>
              Done
            </button>
          </div>
        </div>
      )}
    </footer>
  )
}

export default Footer